package co.maytes.standupbot.dashboard.features.standups

import co.maytes.standupbot.shared.dto.standup.AtRiskSettingsDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto

object AtRiskForm {
    const val STALE_HINT =
        "Flag a started Linear issue when nothing has moved for this many working days. Leave blank to turn it off."
    const val PR_HINT =
        "Flag an open pull request that nobody has reviewed after this many working days. Leave blank to turn it off."
    const val BLOCKED_LABEL = "Include issues marked blocked"
    const val RULE_NOTE =
        "Working days skip weekends and this standup's holidays. Nothing changes until the next run, and each item is listed at most once a week, three times in total."
    const val INVALID = "Enter a whole number from 1 to 10, or leave blank."

    data class Draft(
        val staleIssueDays: String,
        val prReviewWaitDays: String,
        val includeBlocked: Boolean,
    )

    fun draftOf(saved: AtRiskSettingsDto): Draft =
        Draft(
            staleIssueDays = saved.staleIssueDays?.toString().orEmpty(),
            prReviewWaitDays = saved.prReviewWaitDays?.toString().orEmpty(),
            includeBlocked = saved.includeBlocked,
        )

    fun error(raw: String): String? {
        val trimmed = raw.trim()
        if (trimmed.isEmpty()) return null
        val value = trimmed.toIntOrNull()
        return if (value != null && value in AtRiskSettingsDto.MIN_DAYS..AtRiskSettingsDto.MAX_DAYS) null else INVALID
    }

    fun settings(draft: Draft): AtRiskSettingsDto? =
        if (error(draft.staleIssueDays) != null || error(draft.prReviewWaitDays) != null) {
            null
        } else {
            AtRiskSettingsDto(
                staleIssueDays = draft.staleIssueDays.trim().toIntOrNull(),
                prReviewWaitDays = draft.prReviewWaitDays.trim().toIntOrNull(),
                includeBlocked = draft.includeBlocked,
            )
        }

    fun isDirty(
        draft: Draft,
        saved: AtRiskSettingsDto,
    ): Boolean = settings(draft)?.let { it != saved } ?: (draft != draftOf(saved))

    fun canSave(
        draft: Draft,
        saved: AtRiskSettingsDto,
    ): Boolean = settings(draft)?.let { it != saved } ?: false

    fun request(draft: Draft): UpdateStandupRequestDto? = settings(draft)?.let { UpdateStandupRequestDto(atRisk = it) }

    fun statusLine(saved: AtRiskSettingsDto): String =
        if (!saved.enabled) {
            "Off. This standup does not flag stale work."
        } else {
            listOfNotNull(
                saved.staleIssueDays?.let { "issues idle $it working ${plural(it)}" },
                saved.prReviewWaitDays?.let { "pull requests unreviewed $it working ${plural(it)}" },
                "issues marked blocked".takeIf { saved.includeBlocked },
            ).joinToString(", ", prefix = "On. Flags ")
        }

    private fun plural(days: Int): String = if (days == 1) "day" else "days"
}
